import { useI18n } from "@expertloop/i18n/react";
import type { AdminMediaFile, AdminMediaVideo } from "@expertloop/shared";

interface MediaLinksProps {
	files: AdminMediaFile[];
	videos?: AdminMediaVideo[];
	busyId: string | null;
	onOpenFile: (file: AdminMediaFile) => void;
	onOpenVideo: (video: AdminMediaVideo) => void;
}

function iconFor(file: AdminMediaFile): string {
	if (file.voice) return "fa-microphone";
	if (file.mimeType === "application/pdf") return "fa-file-pdf";
	if (file.mimeType.startsWith("image/")) return "fa-file-image";
	if (file.mimeType.startsWith("audio/")) return "fa-file-audio";
	if (file.mimeType.startsWith("video/")) return "fa-file-video";
	if (file.mimeType.includes("word")) return "fa-file-word";
	if (file.mimeType.includes("sheet") || file.mimeType.includes("excel")) return "fa-file-excel";
	return "fa-file";
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds: number | undefined): string {
	if (seconds === undefined) return "";
	const whole = Math.max(0, Math.round(seconds));
	return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * Pièces d'un message ou d'une livraison : fichiers, vocaux, vidéos. Un clic ouvre la pièce dans
 * le lecteur, à côté du rapport et de la décision.
 */
export function MediaLinks({ files, videos = [], busyId, onOpenFile, onOpenVideo }: MediaLinksProps) {
	const { t } = useI18n();
	if (files.length === 0 && videos.length === 0) return null;

	return (
		<ul className="media-links">
			{files.map((file) => (
				<li key={file.id}>
					<button type="button" className="media-link" disabled={busyId === file.id} onClick={() => onOpenFile(file)}>
						<i className={`fas ${iconFor(file)}`} aria-hidden="true"></i>
						<span>
							{file.voice ? t("adminChatDetail.media.voice", { duration: formatDuration(file.durationSeconds) }).trim() : file.originalFilename}
						</span>
						<span className="media-link-meta">{file.voice ? "" : formatSize(file.size)}</span>
						{file.kind === "delivery_preview" && <span className="mediation-chip">{t("adminChatDetail.media.previewFile")}</span>}
					</button>
				</li>
			))}
			{videos.map((video) => (
				<li key={video.id}>
					<button type="button" className="media-link" disabled={busyId === video.id} onClick={() => onOpenVideo(video)}>
						<i className="fas fa-film" aria-hidden="true"></i>
						<span>{video.source === "screen_recording" ? t("adminChatDetail.media.screenRecording") : t("adminChatDetail.media.video")}</span>
						<span className="media-link-meta">{formatDuration(video.durationSeconds)}</span>
						{video.preview && <span className="mediation-chip">{t("adminChatDetail.media.previewFile")}</span>}
					</button>
				</li>
			))}
		</ul>
	);
}
